import React from 'react';
import PropTypes from 'prop-types';

import { useTranslation } from 'react-i18next';
import Seats from './seats';
import { Message, Button } from '@weezevent/nacre';

import { computeContingentSales, computeCategorySales } from '../../helpers/contingentSales';
import AvailabilityZoneTable from '../zoneAvailability/components/availabilityZoneTable';
import css from './seats.module.css';

export function SeatMap({
    zones,
    zonesErrors,
    organization,
    chartId,
    searchZone,
    availability,
    contingents,
    categories,
    rates = [],
    sync = () => {},
    onBack = () => {},
    onZoneSelected = () => {},
    onSeatsSelected = () => {},
    display
}) {
    const { t } = useTranslation();

    const _onSeatsSelected = React.useCallback(
        seats => {
            if (!Array.isArray(seats)) {
                seats = [seats];
            }
            onSeatsSelected(seats);
        },
        [onSeatsSelected]
    );

    const isDetailView = Boolean(availability?.seats?.length);

    if (zonesErrors) {
        return (
            <Message className={css['seat-map-error']} type="danger" title={t('ticket.availability.zones_fetch_failed')}>
                <p>{t('ticket.availability.retrieval_error')}</p>
                <Button
                    inverted
                    onClick={() => {
                        window.location.reload();
                    }}
                    label={t('ticket.common.cta.refresh')}
                />
            </Message>
        );
    }
    // Show zone list
    if (!isDetailView && zones) {
        return (
            <AvailabilityZoneTable
                zones={zones}
                categories={categories}
                contingents={contingents}
                organization={organization}
                chartId={chartId}
                displayMode={display}
                search={searchZone}
                handleOpenZone={onZoneSelected}
            />
        );
    }

    // Show seats detail
    if (isDetailView) {
        return (
            <div className={css['seat-map-container']}>
                <Seats
                    availability={availability}
                    contingents={contingents}
                    categories={categories}
                    rates={rates}
                    onSeatsSelected={_onSeatsSelected}
                    onBack={onBack}
                    onContingentChanged={sync}
                    display={display}
                />
            </div>
        );
    }

    return null;
}

SeatMap.propTypes = {
    zones: PropTypes.array,
    zoneId: PropTypes.string,
    organization: PropTypes.object.isRequired,
    chartId: PropTypes.string,
    eventId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    searchZone: PropTypes.string,
    availability: PropTypes.object,
    contingents: PropTypes.array.isRequired,
    categories: PropTypes.array.isRequired,
    rates: PropTypes.array.isRequired,
    sync: PropTypes.func,
    onBack: PropTypes.func,
    onZoneSelected: PropTypes.func,
    onSeatsSelected: PropTypes.func,
    display: PropTypes.string
};

export function ContingentQuota({ organization, event, contingents, availability, inventory }) {
    const { t } = useTranslation();

    if (!availability || !contingents?.length) {
        return null;
    }

    let contingentComponents = computeContingentSales({
        availability,
        mainContingents: contingents,
        inventory,
        organization,
        event
    });

    if (!contingentComponents?.length) {
        return t('ticket.availability.no_contingents');
    }

    return <ul className="contingent vertical">{contingentComponents}</ul>;
}

export function CategoryQuota({ organization, event, categories, availability, inventory }) {
    const { t } = useTranslation();

    if (!availability || !categories?.length) {
        return null;
    }

    let contingentComponents = computeCategorySales({
        availability,
        categories,
        inventory,
        organization,
        event
    });

    if (!contingentComponents?.length) {
        return t('ticket.availability.no_categories');
    }

    return <ul className="contingent vertical">{contingentComponents}</ul>;
}

ContingentQuota.propTypes = {
    organization: PropTypes.object.isRequired,
    event: PropTypes.object.isRequired,
    contingents: PropTypes.array.isRequired,
    availability: PropTypes.object.isRequired,
    inventory: PropTypes.object.isRequired
};

CategoryQuota.propTypes = {
    organization: PropTypes.object.isRequired,
    event: PropTypes.object.isRequired,
    categories: PropTypes.array.isRequired,
    availability: PropTypes.object.isRequired,
    inventory: PropTypes.object.isRequired
};
